/* eslint-disable react-hooks/exhaustive-deps */
import {
  AvailableLanguages,
  AvailableTimeSlotData,
  DatesWithPrioritizedProviders,
  FeatureManager,
  getSSBLanguage,
  LeadSystemSource,
  ProviderRole,
  scheduleClient,
  ScheduledAppointments,
  SchedulingWorkflow,
  Step,
  storeSSBLanguage,
  SupportedAppointmentIntention,
  SupportedLocationType,
} from '@enaratech/funnel-helper';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { DateTime } from 'luxon';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingIndicator from 'src/components/Common/LoadingIndicator/LoadingIndicator';
import { Page, withProgress } from 'src/components/Common/Progress/Progress';
import Toast from 'src/components/Common/Toast/Toast';
import BasicLayout from 'src/components/Layout/BasicLayout/BasicLayout';
import { useAuth } from 'src/contexts/auth/index';
import { useClinic } from 'src/contexts/clinic/index';
import { useOnboarding } from 'src/contexts/onboarding';
import { useSSB } from 'src/contexts/ssb';
import { SET_SSB_INFO, SSBFlowState } from 'src/contexts/ssb/types';
import { useRoutePath } from 'src/hooks/useRoutePath';
import { CacheDuration, withCache } from 'src/lib/local-cache';
import { getEffectiveProgramType } from 'src/utils/programType';
import { capitalizeToKebabCase } from 'src/utils/array';
import AppointmentsScheduler, {
  AppointmentsSchedulerRef,
} from '../Scheduler/AppointmentsScheduler/AppointmentsScheduler';
import { DEFAULT_DAYS_TO_SKIP_BETWEEN_APPOINTMENTS } from './constants';
import LanguageSelector from './LanguageSelector';
import LocationSelector, {
  LocationSelectorType,
  RADIO_LOCATION_SELECTOR_OPTIONS,
} from './LocationSelector';
import { cleanAndFormatSlots, formatPrioritizedProvidersByDates } from './rules/appointments';
import {
  configureLocationAndSelectorVisibility,
  getPreviousAppointmentByRules,
} from './rules/ssbFlow/actions/fetchStatusAction';
import { evaluateCurrentState } from './rules/ssbFlow/flow';
import { AvailableProvider } from './rules/ssbFlow/types';
import { SchedulingSuggestion } from './ScheduleSuggestion';
import './scss/selfServeBooking.scss';
import SSBTitle from './SSBTitle';

const MIN_DAYS_TO_DISPLAY_ALERT = 5;

export const SelfServeBooking: FC = () => {
  const [selectedTimeSlotKey, setSelectedTimeSlotKey] = useState<string | null>(null); // timeSlot-acuityCalendarId
  const [scheduledAppointments, setScheduledAppointments] = useState<ScheduledAppointments | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [pullingAppointments, setPullingAppointments] = useState<boolean>(true);
  const [availableProviders, setAvailableProviders] = useState<AvailableProvider[] | null>(null);
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState<boolean>(!getSSBLanguage());
  const [scheduleSuggestionIsOpen, setScheduleSuggestionIsOpen] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<AvailableLanguages | null>(getSSBLanguage);
  const [thereIsNoAvailability, setThereIsNoAvailability] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<SupportedLocationType | undefined>('OL');
  const [currentStep, setCurrentStep] = useState<Step | null>(null);
  const [skipConfirmationOpen, setSkipConfirmationOpen] = useState<boolean>(false);

  const appointmentsSchedulerRef = useRef<AppointmentsSchedulerRef | null>(null);
  const stepsRef = useRef<Step[] | null>(null);
  const previousStepPositionRef = useRef<number | null>(null);

  const navigate = useNavigate();

  const {
    authState: { user },
  } = useAuth();

  const { ssbState, recentlyBookedAppointments, dispatchSSB } = useSSB();

  const { clinicState } = useClinic();
  const { dispatchOnboarding } = useOnboarding();
  const routePath = useRoutePath();

  const choseLocationBySpecialtyOrIntention = (
    specialty: ProviderRole,
    intention: SupportedAppointmentIntention,
    defaultValue?: SupportedLocationType
  ): SupportedLocationType | undefined => {
    if ([intention, specialty].includes('GC' as SupportedAppointmentIntention)) {
      return 'OL' as SupportedLocationType;
    }

    if (specialty === ('NS' as ProviderRole)) {
      return 'OL' as SupportedLocationType;
    }

    if (specialty === ('DIA' as ProviderRole)) {
      return 'IP' as SupportedLocationType;
    }

    return defaultValue;
  };

  /**
   * @description STEP 3: Get available dates of the current month
   */
  const fetchAvailableDatesByMonth = useCallback(
    async (month: string | null): Promise<DatesWithPrioritizedProviders | null> => {
      if (!month || !stepsRef.current || !selectedLanguage || !currentStep) {
        return null;
      }

      const clinicId = clinicState!.details.clinicId;

      const { allowedIntention, allowedSpecialty } = currentStep.config;

      const payload = {
        specialty: allowedSpecialty,
        month,
        appointmentIntention: allowedIntention,
        language: selectedLanguage,
        clinicIds: [clinicId],
        usePriorities: await FeatureManager.showPrioritizedAppointments(),
        ...(user?.systemSource && {
          systemSource: capitalizeToKebabCase(user?.systemSource) as LeadSystemSource,
        }),
        location: choseLocationBySpecialtyOrIntention(
          allowedSpecialty,
          allowedIntention,
          selectedLocation
        ),
        schedulingWorkflow: SchedulingWorkflow.SelfServeBooking,
      };

      const response = await withCache(
        `dates-${JSON.stringify(payload, null, 0)}`,
        () => scheduleClient.getPrioritizedMonthlyProviderAvailability(payload),
        CacheDuration.Short
      );

      if (!response) {
        return null;
      }

      const prioritizedDatesWithProviders = formatPrioritizedProvidersByDates(response.dates);

      if (!prioritizedDatesWithProviders) {
        return null;
      }

      return prioritizedDatesWithProviders;
    },
    [clinicState, currentStep, selectedLanguage, selectedLocation, user?.systemSource]
  );

  /**
   * @description STEP 4: Get available time slots of a specific date (the first time takes the current day + days of space per rules)
   */
  const fetchTimeSlotsByDate = useCallback(
    async (
      currentDate: DateTime,
      availableProviders: AvailableProvider[]
    ): Promise<AvailableTimeSlotData[] | null> => {
      if (!stepsRef.current || !selectedLanguage || !currentStep) {
        return null;
      }

      setAvailableProviders(availableProviders);

      const { allowedIntention, allowedSpecialty } = currentStep.config;

      const payload = {
        clinicIds: [clinicState!.details.clinicId],
        specialty: allowedSpecialty,
        date: currentDate.toFormat('yyyy-MM-dd'),
        appointmentIntention: allowedIntention,
        language: selectedLanguage,
        usePriorities: await FeatureManager.showPrioritizedAppointments(),
        ...(user?.systemSource && {
          systemSource: capitalizeToKebabCase(user?.systemSource) as LeadSystemSource,
        }),
        location: choseLocationBySpecialtyOrIntention(
          allowedSpecialty,
          allowedIntention,
          selectedLocation
        ),
        schedulingWorkflow: SchedulingWorkflow.SelfServeBooking,
      };

      const availableAppointments = await withCache(
        `times-${JSON.stringify(payload, null, 0)}`,
        () => scheduleClient.getPrioritizedDailyProviderAvailability(payload),
        CacheDuration.VeryShort
      );

      if (!availableAppointments) {
        return null;
      }

      const prioritizedTimeSlots = cleanAndFormatSlots(
        availableAppointments,
        DateTime.local().zoneName
      );

      return prioritizedTimeSlots;
    },
    [currentStep, selectedLanguage, selectedLocation, clinicState, user?.systemSource]
  );

  const getPreviousAppointmentByRulesUseCallback = useCallback(getPreviousAppointmentByRules, [
    selectedLanguage,
    stepsRef.current,
  ]);

  /**
   * @description STEP 2: Initialize the calendar to obtain dates and time slots
   */
  const resetCalendar = () => {
    if (
      !clinicState ||
      !stepsRef.current ||
      !scheduledAppointments ||
      !selectedLanguage ||
      !currentStep ||
      isSubmitting
    ) {
      return <LoadingIndicator />;
    }

    // Don't render scheduler while language selector modal is open
    const shouldWaitForLanguageSelection =
      currentStep.config.allowedLanguages.length > 1 && isLanguageSelectorOpen;

    if (shouldWaitForLanguageSelection) {
      return <LoadingIndicator />;
    }

    if (currentStep && currentStep?.config.allowedLocations.length > 1 && !selectedLocation) {
      return <LoadingIndicator />;
    }

    const previousAppointmentByRules = getPreviousAppointmentByRulesUseCallback({
      scheduledAppointments,
      stepsRef,
      selectedLanguage,
    });

    return (
      <AppointmentsScheduler
        ref={appointmentsSchedulerRef}
        onNoAvailabilityFound={(value) => {
          const hasMultipleLanguages = currentStep && currentStep?.config.allowedLanguages.length > 1;
          const shouldShowToast = value && (!hasMultipleLanguages || !isLanguageSelectorOpen);

          if (shouldShowToast) {
            Toast.notification('info', 'No appointments are currently available. You can skip this step using the button at the bottom of the page.', {
              draggable: false,
              closeOnClick: true,
              autoClose: false,
            });
          }
          setThereIsNoAvailability(value);

          if (hasMultipleLanguages) {
            setIsLanguageSelectorOpen(value);
          }
        }}
        initialDaysToSkip={DEFAULT_DAYS_TO_SKIP_BETWEEN_APPOINTMENTS}
        previousAppointment={previousAppointmentByRules}
        onSelectHour={setSelectedTimeSlotKey}
        getCurrentAvailability={fetchTimeSlotsByDate}
        getAvailableDatesInMonth={fetchAvailableDatesByMonth}
      />
    );
  };

  const handleLanguageSelection = (pickedLanguage: AvailableLanguages | null) => {
    if (pickedLanguage) {
      setSelectedLanguage(pickedLanguage);
      storeSSBLanguage(pickedLanguage);
      setIsLanguageSelectorOpen(false);
      dispatchSSB({ type: SET_SSB_INFO, payload: SSBFlowState.InitSchedulingProcess });
    }
  };

  /**
   * @description
   * STEP 0: Select a language (render language selector)
   * STEP 1: Set stepsRef.current by rules (enabled clinic + programType) with current status
   * STEP 2: Initialize the calendar to obtain dates and time slots
   * STEP 3: Get available dates of the current month
   * STEP 4: Get available time slots of a specific date (the first time takes the current day + days of space per rules)
   * STEP 5: Schedule with chosen date and time slot
   */

  // STEP 1: Steps Logics
  useEffect(() => {
    if (clinicState) {
      evaluateCurrentState(ssbState, {
        context: {
          clinic: clinicState!,
          user,
          routePath,
          navigate,
          stepsRef,
          appointmentsSchedulerRef,
          selectedLanguage,
          scheduledAppointments,
          recentlyBookedAppointments,
          selectedTimeSlotKey,
          availableProviders,
        },
        setters: {
          setSelectedLanguage,
          setScheduledAppointments,
          setPullingAppointments,
          setSelectedLocation,
          setIsSubmitting,
          setAvailableProviders,
          setSelectedTimeSlotKey,
          setThereIsNoAvailability,
          dispatchOnboarding,
        },
        setState: dispatchSSB,
      });

      const currentStep = stepsRef.current?.find((s) => s.active) || null;

      setCurrentStep(currentStep);
    }
  }, [ssbState]);

  useEffect(() => {
    (async () => {
      if (
        currentStep &&
        currentStep.config.allowedLocations.length > 1 &&
        stepsRef.current &&
        selectedLocation &&
        selectedLanguage
      ) {
        const previousAppointment = getPreviousAppointmentByRulesUseCallback({
          scheduledAppointments,
          stepsRef,
          selectedLanguage,
        });

        appointmentsSchedulerRef.current?.resetCalendar(previousAppointment ?? undefined);
      }

      if (currentStep && !selectedLocation) {
        configureLocationAndSelectorVisibility({
          context: { stepConfig: currentStep.config },
          setters: {
            setSelectedLocation,
          },
        });
      }
    })();
  }, [selectedLocation, stepsRef.current]);

  useEffect(() => {
    if (!selectedTimeSlotKey) {
      return;
    }

    if (DateTime.fromISO(selectedTimeSlotKey).diffNow('days').days > MIN_DAYS_TO_DISPLAY_ALERT) {
      setScheduleSuggestionIsOpen(true);
    }
  }, [selectedTimeSlotKey]);

  // When the flow advances to a different step, drop any slot selection made against the previous
  // step. Booking clears the selection, but the just-booked step's calendar stays interactive
  // during the ~3s delay before the flow advances; a slot picked there would otherwise survive the
  // transition and be submitted against the NEW step — createAppointmentAction books the slot's
  // appointment type, not the step's, so this created a duplicate wrong-type appointment (observed:
  // a 2nd IN-NS booked while the flow had already advanced to the Group Class step).
  useEffect(() => {
    const position = currentStep?.position ?? null;

    if (previousStepPositionRef.current !== null && previousStepPositionRef.current !== position) {
      setSelectedTimeSlotKey(null);
      setAvailableProviders(null);
      setThereIsNoAvailability(false);
    }

    previousStepPositionRef.current = position;
  }, [currentStep?.position]);

  if (!stepsRef.current) {
    return <LoadingIndicator />;
  }

  const isFirstAppointmentStep = (): boolean => {
    if (!stepsRef.current) {
      return true;
    }

    const scheduledSteps = stepsRef.current.filter((s) => s.scheduled);

    return scheduledSteps.length === 0;
  };

  const renderLanguageSelector = (
    shouldDisplayOpened: boolean,
    showMessageForNoAvailability: boolean
  ) => {
    return (
      <LanguageSelector
        isLanguageSelectorOpen={shouldDisplayOpened}
        onSelect={handleLanguageSelection}
        noAvailableAppointments={showMessageForNoAvailability}
        selectedLanguage={selectedLanguage}
        allowedLanguages={currentStep?.config.allowedLanguages || [AvailableLanguages.English]}
        onClickLink={setIsLanguageSelectorOpen}
        isFirstAppointmentStep={isFirstAppointmentStep()}
      />
    );
  };

  return (
    <>
      <SSBTitle
        steps={stepsRef.current}
        programType={getEffectiveProgramType({ user, clinic: clinicState })!}
        pullingAppointments={pullingAppointments}
      />
      <BasicLayout
        {...(availableProviders && {
          buttonProps: {
            text: 'Confirm appointment',
            disabled: !selectedTimeSlotKey,
            loading: isSubmitting,
            onClick: () =>
              dispatchSSB({ type: SET_SSB_INFO, payload: SSBFlowState.CreateAppointment }),
          },
        })}
        {...(currentStep?.config.skippable && thereIsNoAvailability && {
          secondaryButtonProps: {
            text: 'Skip appointment',
            disabled: isSubmitting,
            loading: isSubmitting,
            variant: 'text',
            onClick: () => setSkipConfirmationOpen(true),
          },
        })}>
        {ssbState === SSBFlowState.WaitingForUserSelection &&
          currentStep &&
          currentStep?.config.allowedLanguages.length > 1 &&
          renderLanguageSelector(
            isLanguageSelectorOpen || !selectedLanguage,
            thereIsNoAvailability
          )}

        {pullingAppointments ? (
          <Stack display='flex' alignItems={'center'} flexDirection={'column'}>
            <LoadingIndicator />
          </Stack>
        ) : (
          <>
            {currentStep && currentStep?.config.allowedLocations.length > 1 ? (
              <>
                <LocationSelector
                  onChange={setSelectedLocation}
                  selectedLocation={selectedLocation}
                  type={LocationSelectorType.Selector}
                  options={RADIO_LOCATION_SELECTOR_OPTIONS}
                />
                {selectedLocation && resetCalendar()}
              </>
            ) : (
              resetCalendar()
            )}

            {scheduleSuggestionIsOpen && (
              <SchedulingSuggestion
                open={scheduleSuggestionIsOpen}
                onClose={setScheduleSuggestionIsOpen}
              />
            )}
          </>
        )}
      </BasicLayout>

      <Dialog
        open={skipConfirmationOpen}
        onClose={() => setSkipConfirmationOpen(false)}
        aria-labelledby='skip-step-dialog-title'
        aria-describedby='skip-step-dialog-description'
        PaperProps={{
          sx: {
            padding: '24px',
            borderRadius: '12px',
            minWidth: '320px',
            textAlign: 'center',
          },
        }}>
        <DialogTitle
          id='skip-step-dialog-title'
          sx={{ fontSize: '20px', fontWeight: 600, padding: '0 0 16px 0' }}>
          Skip appointment?
        </DialogTitle>
        <DialogContent sx={{ padding: '0 0 24px 0' }}>
          <DialogContentText
            id='skip-step-dialog-description'
            sx={{ fontSize: '18px', textAlign: 'center' }}>
            Are you sure you want to skip this appointment? You can always schedule it later.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', padding: 0, gap: '16px', margin: '5px' }}>
          <Button
            onClick={() => setSkipConfirmationOpen(false)}
            variant='outlined'
            sx={{ minWidth: '100px' }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setSkipConfirmationOpen(false);
              dispatchSSB({ type: SET_SSB_INFO, payload: SSBFlowState.SkipStep });
            }}
            variant='contained'
            sx={{ minWidth: '100px' }}>
            Skip
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withProgress(SelfServeBooking, Page.SelfServeBooking);
